import { AlertModel } from '../models/alert.model';
import { AlertService } from './alert.service';
import { CountryModel } from '../models/country.model';
import { CountryService } from './country.service';
import { CredentialModel, CredentialType } from '../models/credential.model';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SiteModel } from '../models/site.model';
import { SiteService } from './site.service';
import { Subject, Subscription } from 'rxjs';
import { UserModel } from '../models/user.model';
import { UserSearchRequest, UserSearchResult } from '../models/user-search-request.model';
import { UserService } from './user.service';

/**
 * This service provides centralized administration functionality
 * for the camera application, aggregating data from various services
 */
@Injectable({
  providedIn: 'root',
})
export class AdminService {
  isReady: boolean;
  credentials: CredentialModel[] = [];

  /**
   * Get all users
   * @returns {UserModel[]} - List of users
   */
  get users(): UserModel[] {
    return this.userService.users;
  }

  /**
   * Get all user with countries
   * @returns {UserModel[]} - List of users with countries
   */
  get usersWithCountry(): UserModel[] {
    return this.userService.usersWithCountry
  }

  /**
   * Get all sites
   * @returns {SiteModel[]} - List of sites
   */
  get sites(): SiteModel[] {
    return this.siteService.sites;
  }

  /**
   * Get all alerts
   * @returns {AlertModel[]} - List of alerts
   */
  get alerts(): AlertModel[] {
    return this.alertService.alerts;
  }

  /**
   * Get all server credentials
   * @returns {CredentialModel[]} - List of server credentials
   */
  get serverCredentials(): CredentialModel[] {
    return this.credentials.filter((c) => c.Type == CredentialType.Server);
  }

  /**
   * Get all camera credentials
   * @returns {CredentialModel[]} - List of camera credentials
   */
  get cameraCredentials(): CredentialModel[] {
    return this.credentials.filter((c) => c.Type == CredentialType.Camera);
  }

  /**
   * Get all countries
   * @returns {CountryModel[]} - List of countries
   */
  get countries(): CountryModel[] {
    return this.countryService.countries;
  }

  // Events
  private adminServiceLoadedSource = new Subject<void>();

  /**
   * Observable that emits when the admin service has finished loading all data
   */
  public adminServiceLoadedEvent = this.adminServiceLoadedSource.asObservable();

  // Event Handlers
  private usersRefreshedHandler: Subscription;

  constructor(
    private http: HttpClient,
    private alertService: AlertService,
    private userService: UserService,
    private siteService: SiteService,
    private countryService: CountryService
  ) {
    this.initService();
  }

  /**
   * Initialize the admin service by loading all required data
   */
  private initService() {
    this.isReady = false;
    Promise.all([
      this.userService.getUsers(true),
      this.userService.getUsersWithCountries(true, true),
      this.siteService.getAllSites(true, true),
      this.getCredentials(),
      this.alertService.getAllAlerts(),
      this.countryService.getAllCountries()
    ])
      .then((results) => {
        this.userService.users = results[0];
        this.userService.usersWithCountry = results[1]
        this.siteService.sites = results[2];
        this.credentials = results[3];
        this.alertService.alerts = results[4];
        this.countryService.countries = results[5];
      })
      .catch((err) => {
        console.error('Error initializing AdminService:', err);
      })
      .finally(() => {
        this.isReady = true;
        this.adminServiceLoadedSource.next();
      });
  }

  /**
   * Reload all service data
   */
  reloadService() {
    this.initService();
  }

  /**
   * Get all credentials from the management API
   * @returns Promise
   */
  async getCredentials(): Promise<CredentialModel[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get<CredentialModel[]>(`${environment.managementApi}/Credentials/All`)
        .subscribe({
          next: (creds) => {
            resolve(creds);
          },
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Search for users using Azure AD search
   * @param  {UserSearchRequest} request - Search request parameters
   * @returns Promise
   */
  async searchForUser(request: UserSearchRequest): Promise<UserSearchResult[]> {
    return new Promise((resolve, reject) => {
      this.http
        .post<UserSearchResult[]>(
          `${environment.managementApi}/azure/search`,
          request
        )
        .subscribe({
          next: (results) => {
            resolve(results);
          },
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Get camera inventory data
   * @returns Promise
   */
  async getCameraInventory(): Promise<SiteModel[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get<SiteModel[]>(`${environment.managementApi}/Sites/inventory`)
        .subscribe({
          next: (sites) => {
            resolve(sites);
          },
          error: (err) => reject(err)
        });
    });
  }
}
